import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useMutation } from '@apollo/client';
import { getError } from '@utils/graph-utils';
import { useParams } from 'react-router-dom';
import { overrideCostAndCharges } from './queries';
import { getCostChargesInputs } from './content';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import { IAbstractRecord } from '@models';
import PrecisionService from '@services/PrecisionService';
import { IAmendmentGeneralData } from '@modules/production/amendment-details/line-amendment';

interface ICostChargesDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  data: IAbstractRecord;
  lineExternalCode: string;
}

const CostChargesDetailsDrawer: React.FC<ICostChargesDetailsDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  data,
  lineExternalCode,
}) => {
  const params = useParams();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [saveCostChargesData] = useMutation(overrideCostAndCharges());

  const inputForm = useMemo(
    () => getCostChargesInputs(data, lineExternalCode),
    [data, lineExternalCode]
  );

  const submitForm = async (formValues: Record<string, any>) => {
    const [formData] = normalizeDynamicFormDrawerValues(inputForm, formValues);
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const { errors } = await saveCostChargesData({
        variables: {
          amendmentId: params.id,
          ChargesVariance: PrecisionService.divideBy100(
            Number(formData.chargesVariance)
          ),
          PolicyCostVariance: Number(formData.policyCostVariance),
          TPAFeesVariance: Number(formData.tpaFeesVariance),
        },
      });

      if (!errors || errors.length === 0) {
        toast.success(
          <ToastSuccessMessage>
            Cost & Charges details updated successfully!
          </ToastSuccessMessage>
        );
        setSubmitButtonState('success');
        onSuccess();
        onClose();
      } else {
        setSubmitButtonState(undefined);
        const errorMessage = getError({ errors });
        toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      }
    } catch (err) {
      const errorMessage = getError({ err });
      toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      setSubmitButtonState(undefined);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      isOpen={open}
      title="Override Cost & Charges"
      sections={inputForm}
      onSubmit={submitForm}
      buttonText="Submit"
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onClose={onClose}
    />
  );
};

export default CostChargesDetailsDrawer;
